import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover } from '@headlessui/react'
import { EyeIcon } from '@heroicons/react/24/outline'
import classnames from 'classnames'
import { linkRateSheetToHistoryApi, listUtilityRateSheetsApi } from '../../../apis/apis'
import type { TariffHistoryAttachment, TariffRateSheetCategory } from '../../../apis/types'
import Button from '../../../components/Form/Button'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { formatUTCDetailedDate } from '../../../util/string'
import { TariffDocumentViewerModal } from './TariffDocumentViewerModal'

const inputClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
const selectClass =
    'block w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 shadow-sm focus:border-indigo-600 focus:ring-indigo-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function formatCategory(category: TariffHistoryAttachment['category']): string {
    return category === 'GROUP' ? 'Group' : 'Individual'
}

function sheetMetaLine(sheet: TariffHistoryAttachment): string {
    const parts = [formatCategory(sheet.category)]
    if (sheet.vintageYear != null && sheet.vintageYear !== '') {
        parts.push(`PCIA ${sheet.vintageYear}`)
    }
    if (sheet.uploadedAt != null) {
        parts.push(formatUTCDetailedDate(new Date(sheet.uploadedAt)))
    }
    return parts.join(' · ')
}

const LinkRateSheetPopoverContent: React.FC<{
    open: boolean
    utilityCuid: string | null
    historyCuid: string | null
    linkedCuids: string[]
    onLinked: (attachment: TariffHistoryAttachment) => void
}> = ({ open, utilityCuid, historyCuid, linkedCuids, onLinked }) => {
    const dispatch = useAppDispatch()
    const [search, setSearch] = useState('')
    const [category, setCategory] = useState<TariffRateSheetCategory | ''>('')
    const [loading, setLoading] = useState(false)
    const [linkingCuid, setLinkingCuid] = useState<string | null>(null)
    const [sheets, setSheets] = useState<TariffHistoryAttachment[]>([])
    const [viewerAttachment, setViewerAttachment] = useState<TariffHistoryAttachment | null>(null)

    const linkedSet = useMemo(() => new Set(linkedCuids), [linkedCuids])

    const loadSheets = useCallback(async (): Promise<void> => {
        if (utilityCuid == null) return
        setLoading(true)
        try {
            const rows = await listUtilityRateSheetsApi(utilityCuid, {
                sharedOnly: true,
                category: category !== '' ? category : undefined,
                search: search.trim() !== '' ? search.trim() : undefined,
            })
            setSheets(rows)
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to load shared rate sheets' }))
        } finally {
            setLoading(false)
        }
    }, [category, dispatch, search, utilityCuid])

    useEffect(() => {
        if (!open) {
            setViewerAttachment(null)
            return
        }
        setSearch('')
        setCategory('')
    }, [open, utilityCuid])

    useEffect(() => {
        if (!open || utilityCuid == null) return
        const timer = window.setTimeout(() => {
            void loadSheets()
        }, 300)
        return () => window.clearTimeout(timer)
    }, [loadSheets, open, utilityCuid])

    const handleLink = async (sheet: TariffHistoryAttachment): Promise<void> => {
        if (historyCuid == null || linkedSet.has(sheet.cuid)) return
        setLinkingCuid(sheet.cuid)
        try {
            const linked = await linkRateSheetToHistoryApi(historyCuid, sheet.cuid)
            onLinked(linked)
            void dispatch(pushToast({ type: 'success', message: 'Rate sheet linked' }))
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to link rate sheet' }))
        } finally {
            setLinkingCuid(null)
        }
    }

    if (utilityCuid == null) {
        return (
            <p className="p-3 text-xs text-slate-500 dark:text-slate-400">
                Utility is required to browse shared rate sheets.
            </p>
        )
    }

    return (
        <>
            <div className="border-b border-slate-200 p-3 dark:border-slate-700">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    Link shared rate sheet
                </p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Pick a sheet from this utility&apos;s shared library.
                </p>
                <div className="mt-3 space-y-2">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name"
                        className={inputClass}
                    />
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value as TariffRateSheetCategory | '')}
                        className={selectClass}
                    >
                        <option value="">All categories</option>
                        <option value="INDIVIDUAL">Individual</option>
                        <option value="GROUP">Group</option>
                    </select>
                </div>
            </div>

            <div className="max-h-72 overflow-y-auto">
                {loading ? (
                    <p className="p-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
                ) : sheets.length === 0 ? (
                    <p className="p-3 text-sm text-slate-500 dark:text-slate-400">
                        No shared rate sheets found.
                    </p>
                ) : (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                        {sheets.map((sheet) => {
                            const alreadyLinked = linkedSet.has(sheet.cuid)
                            return (
                                <li
                                    key={sheet.cuid}
                                    className="flex items-start justify-between gap-3 py-2.5 pr-3 text-sm"
                                >
                                    <div className="min-w-0">
                                        <p className="font-medium text-slate-900 dark:text-slate-100">
                                            {sheet.label}
                                        </p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {sheetMetaLine(sheet)}
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setViewerAttachment(sheet)}
                                            className="rounded p-1.5 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                                            aria-label={`View ${sheet.label}`}
                                            title="View"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                        <Button
                                            type="button"
                                            disabled={alreadyLinked || linkingCuid === sheet.cuid}
                                            onClick={() => void handleLink(sheet)}
                                        >
                                            {alreadyLinked
                                                ? 'Linked'
                                                : linkingCuid === sheet.cuid
                                                  ? 'Linking…'
                                                  : 'Link'}
                                        </Button>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
            <TariffDocumentViewerModal
                attachment={viewerAttachment}
                onClose={() => setViewerAttachment(null)}
            />
        </>
    )
}

export const TariffLinkRateSheetPopover: React.FC<{
    utilityCuid: string | null
    historyCuid: string | null
    linkedCuids: string[]
    onLinked: (attachment: TariffHistoryAttachment) => void
}> = ({ utilityCuid, historyCuid, linkedCuids, onLinked }) => (
    <Popover className="relative">
        {({ open }) => (
            <>
                <Popover.Button
                    disabled={utilityCuid == null}
                    className={classnames(
                        'rounded-md border px-3 py-1.5 text-sm font-medium disabled:opacity-50',
                        'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
                        'data-[open]:border-indigo-600 data-[open]:bg-indigo-50 data-[open]:text-indigo-700',
                        'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200',
                        'dark:data-[open]:border-indigo-500 dark:data-[open]:bg-indigo-950/40 dark:data-[open]:text-indigo-200'
                    )}
                >
                    Link shared rate sheet
                </Popover.Button>
                <Popover.Panel
                    anchor={{ to: 'bottom end', gap: 8 }}
                    portal
                    modal={false}
                    transition
                    className="z-30 w-[min(28rem,calc(100vw-2rem))] overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg transition duration-100 ease-out data-[closed]:translate-y-0.5 data-[closed]:opacity-0 dark:border-slate-600 dark:bg-slate-900"
                >
                    <LinkRateSheetPopoverContent
                        open={open}
                        utilityCuid={utilityCuid}
                        historyCuid={historyCuid}
                        linkedCuids={linkedCuids}
                        onLinked={onLinked}
                    />
                </Popover.Panel>
            </>
        )}
    </Popover>
)

export default TariffLinkRateSheetPopover
